import { Link } from "@/components/link";
import { Logo } from "@/components/logo";
import { Facebook, Instagram, Twitter } from "@/components/social-icons";
import { Setting } from "@/lib/types";
import type { SiteSetting } from "@/lib/types";

const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/explore", label: "Explore" },
  { href: "/broadcastSchedule", label: "Broadcast schedule" },
];

const SCHOOL_URL = "https://www.oundleschool.org.uk/";

const SOCIALS = [
  {
    fallback: "https://twitter.com",
    icon: Twitter,
    label: "Twitter",
    type: Setting.TWITTER,
  },
  {
    fallback: "https://instagram.com",
    icon: Instagram,
    label: "Instagram",
    type: Setting.INSTAGRAM,
  },
  {
    fallback: "https://facebook.com",
    icon: Facebook,
    label: "Facebook",
    type: Setting.FACEBOOK,
  },
];

export function Footer({ settings }: { settings: SiteSetting[] }) {
  const urlFor = (type: SiteSetting["type"], fallback: string) =>
    settings.find((it) => it.type === type)?.value ?? fallback;

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-12 lg:px-10">
        <div className="flex flex-col justify-between gap-8 sm:flex-row sm:items-start">
          <div className="flex max-w-sm flex-col gap-3">
            <Logo />
            <p className="text-sm text-primary-foreground/70">
              Student-run radio, broadcasting live and on demand. Made by
              students, for everyone.
            </p>
          </div>

          <nav className="flex flex-col gap-2 text-sm">
            {NAV_LINKS.map((link) => (
              <Link
                className="text-primary-foreground/80 transition-colors hover:text-primary-foreground"
                href={link.href}
                key={link.href}
              >
                {link.label}
              </Link>
            ))}
            <a
              className="text-primary-foreground/80 transition-colors hover:text-primary-foreground"
              href={SCHOOL_URL}
              rel="noreferrer"
              target="_blank"
            >
              Oundle School
            </a>
          </nav>

          <div className="flex items-center gap-4">
            {SOCIALS.map(({ fallback, icon: Icon, label, type }) => (
              <a
                aria-label={label}
                className="text-primary-foreground/70 transition-colors hover:text-primary-foreground"
                href={urlFor(type, fallback)}
                key={label}
                rel="noreferrer"
                target="_blank"
              >
                <Icon className="size-5" />
              </a>
            ))}
          </div>
        </div>

        <p className="border-t border-primary-foreground/10 pt-6 text-xs text-primary-foreground/50">
          © {new Date().getFullYear()} OSCAR Radio. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
